import React, { useEffect, useMemo, useState } from 'react';
import cs from 'classnames';
import { Textarea } from '@arco-design/mobile-react-serial-i18n';
import { getConfigs } from '@service/api';
import { Message } from '@byted/serial-components-i18n';
import { MessageText } from '@constants/value';
import { useTranslation } from '@ies/intl-react-plugin';
import { OtherTitleMap, ScoreLevels } from './constants';

export interface ReasonItem {
    key: string;
    needSuggestion: boolean;
}
interface ReasonProps {
    score: ScoreLevels | undefined;
    reasons: ReasonItem[];
    reasonData: Record<string, ReasonItem[]> | undefined;
    suggestion: string;
    setReasons: (reason: ReasonItem[]) => void;
    setSuggestion: (suggestion: string) => void;
}

const ReasonsKey = 'fizzo_score_reasons_id';

export const useReasonData = () => {
    const [reasonData, setReasonData] = useState<Record<string, ReasonItem[]>>();

    useEffect(() => {
        getReasonList();
    }, []);

    const getReasonList = async () => {
        try {
            const resp: any = await getConfigs({ keys: ReasonsKey });

            if (resp.code) {
                throw Error();
            }
            const reasons = resp.data[ReasonsKey] as Record<number, []>;
            Object.keys(reasons).forEach(k => {
                const item = reasons[k] as string[];
                reasons[k] = item.map(str => {
                    // 处理"starling_key|1", "|1"表示需要输入框
                    const [key, needSuggestion] = str.split('|');
                    return { key, needSuggestion: needSuggestion === '1' };
                });
            });
            setReasonData(reasons);
        } catch (e) {
            console.log(e);
            Message.error(MessageText.netError);
        }
    };

    return reasonData;
};

const Reasons: React.FC<ReasonProps> = ({ score, reasons, suggestion, reasonData, setReasons, setSuggestion }) => {
    const { t } = useTranslation();

    const renderReason = (reason: ReasonItem) => {
        const { key } = reason;

        const selected = reasons.some(item => item.key === key);
        return (
            <div
                className={cs('reason-text', { 'reason-text--selected': selected })}
                onClick={() => {
                    if (selected) {
                        setReasons(reasons.filter(item => item.key !== key));
                    } else {
                        setReasons([...reasons, reason]);
                    }
                }}
            >
                {t(key, key)}
            </div>
        );
    };

    const showTextarea = useMemo(() => reasons.some(item => item.needSuggestion), [reasons]);

    useEffect(() => {
        if (!showTextarea) {
            setSuggestion('');
        }
    }, [showTextarea]);

    return (
        <>
            {reasonData && score ? (
                <div className="article-score-reasons">
                    {reasonData[String(score)]?.map(item => renderReason(item))}
                </div>
            ) : null}
            {showTextarea && (
                <div className="article-score-suggestion">
                    <p>
                        {t(OtherTitleMap[score as ScoreLevels], 'other')} <span>{suggestion.length}/500</span>
                    </p>
                    <Textarea
                        showStatistics={false}
                        maxLength={500}
                        value={suggestion}
                        onChange={e => setSuggestion(e.target.value)}
                        placeholder={t('academy_rating_direction')}
                    />
                </div>
            )}
        </>
    );
};

export default Reasons;
